// src/config/axiosConfig.js - Centralized Axios Configuration
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL;

class AxiosConfig {
  constructor() {
    this.axiosInstance = null;
    this.setupAxios();
  }

  setupAxios() {
    // Create axios instance
    this.axiosInstance = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 30000,
    });

    // Setup interceptors
    this.setupRequestInterceptor();
    this.setupResponseInterceptor();
  }

  setupRequestInterceptor() {
    this.axiosInstance.interceptors.request.use(
      (config) => {
        // Auto-check token expiration before every request
        const token = this.getAuthToken();
        
        if (token) {
          // Check if token is expired before making the request
          if (this.isTokenExpired(token)) {
            this.removeAuthToken();
            this.handleUnauthorized();
            
            // Reject the request with a specific error
            return Promise.reject(new Error('Your session has expired. Please log in again.'));
          }
          
          // Token is valid, add to headers
          config.headers.Authorization = `Bearer ${token}`;
        }
        
        return config;
      },
      (error) => Promise.reject(error)
    );
  }

  setupResponseInterceptor() {
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      (error) => {
        // Handle different types of auth-related errors
        if (error.response?.status === 401) {
          this.removeAuthToken();
          this.handleUnauthorized();
        }
        
        // Also handle 403 as potential auth issue
        if (error.response?.status === 403) {
          // Don't automatically remove token for 403, but notify
          this.handleForbidden();
        }
        
        return Promise.reject(this.handleError(error));
      }
    );
  }

  // Token management methods
  getAuthToken() {
    try {
      return localStorage.getItem('dubvideo_auth_token');
    } catch (error) {
      return null;
    }
  }

  setAuthToken(token) {
    try {
      localStorage.setItem('dubvideo_auth_token', token);
    } catch (error) {
      throw new Error('Failed to save authentication token');
    }
  }

  removeAuthToken() {
    try {
      localStorage.removeItem('dubvideo_auth_token');
    } catch (error) {
      console.error('Error removing auth token:', error);
    }
  }

  isTokenExpired(token = null) {
    const authToken = token || this.getAuthToken();
    if (!authToken) return true;

    try {
      const payload = JSON.parse(atob(authToken.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      return payload.exp && payload.exp < currentTime;
    } catch (error) {
      console.error('Error checking token expiration:', error);
      return true;
    }
  }

  isAuthenticated() {
    const token = this.getAuthToken();
    return token && !this.isTokenExpired(token);
  }

  // Handle unauthorized access
  handleUnauthorized() {
    // Dispatch custom event for unauthorized access
    window.dispatchEvent(new CustomEvent('auth:unauthorized', {
      detail: { 
        message: 'Your session has expired. Please log in again.',
        timestamp: Date.now()
      }
    }));
  }

  // Handle forbidden access (403)
  handleForbidden() {
    // Dispatch custom event for forbidden access
    window.dispatchEvent(new CustomEvent('auth:forbidden', {
      detail: { 
        message: 'You don\'t have permission to perform this action.',
        timestamp: Date.now()
      }
    }));
  }

  // Check if token is about to expire (within 5 minutes)
  isTokenExpiringSoon(token = null, minutesThreshold = 5) {
    const authToken = token || this.getAuthToken();
    if (!authToken) return true;

    try {
      const payload = JSON.parse(atob(authToken.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      const thresholdTime = currentTime + (minutesThreshold * 60);
      
      return payload.exp && payload.exp < thresholdTime;
    } catch (error) {
      console.error('Error checking token expiration:', error);
      return true;
    }
  }

  // Get token expiration time
  getTokenExpirationTime(token = null) {
    const authToken = token || this.getAuthToken();
    if (!authToken) return null;

    try {
      const payload = JSON.parse(atob(authToken.split('.')[1]));
      return payload.exp ? new Date(payload.exp * 1000) : null;
    } catch (error) {
      console.error('Error getting token expiration time:', error);
      return null;
    }
  }

  // Get time until token expires (in seconds)
  getTimeUntilExpiration(token = null) {
    const authToken = token || this.getAuthToken();
    if (!authToken) return 0;

    try {
      const payload = JSON.parse(atob(authToken.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      return payload.exp ? Math.max(0, payload.exp - currentTime) : 0;
    } catch (error) {
      console.error('Error calculating time until expiration:', error);
      return 0;
    }
  }

  // Enhanced error handling
  handleError(error) {
    if (error.response) {
      const data = error.response.data;
      const status = error.response.status;
      
      switch (status) {
        case 401:
          return new Error('Your session has expired. Please log in again.');
        case 403:
          return new Error('You don\'t have permission to perform this action.');
        case 404:
          return new Error('The requested resource was not found.');
        case 409:
          return new Error(data?.detail || 'A conflict occurred. Please try again.');
        case 422:
          return new Error(data?.detail || 'Invalid data provided.');
        case 429:
          return new Error('Too many requests. Please wait a moment and try again.');
        case 500:
        case 502:
        case 503:
        case 504:
          return new Error('Server error. Please try again later.');
        default:
          return new Error(data?.detail || data?.message || `Request failed with status ${status}`);
      }
    } else if (error.request) {
      return new Error('Network error: Unable to connect to server. Please check your connection.');
    } else if (error.message === 'Your session has expired. Please log in again.') {
      // This is our custom token expiration error from request interceptor
      return error;
    } else {
      return new Error(error.message || 'An unexpected error occurred');
    }
  }

  // Load stored token on initialization
  loadStoredToken() {
    try {
      const storedToken = this.getAuthToken();
      if (storedToken && this.isTokenExpired(storedToken)) {
        this.removeAuthToken();
      }
    } catch (error) {
      console.error('Error loading stored token:', error);
      this.removeAuthToken();
    }
  }

  // Get the axios instance
  getInstance() {
    return this.axiosInstance;
  }

  // Get base URL
  getBaseURL() {
    return API_BASE_URL;
  }
}

// Create and export a singleton instance
const axiosConfig = new AxiosConfig();

// Load stored token on initialization
axiosConfig.loadStoredToken();

export default axiosConfig;